<script setup lang="ts">
import LayoutDefaultHeader from '@/core/components/layouts/components/LayoutDefaultHeader.vue';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/core/stores/useAppStore';
import { PrimeIcons } from '@primevue/core/api';
import { Button } from 'primevue';
import UserProfileMenu from '@/core/components/layouts/components/UserProfileMenu.vue';
import controlTowerLogo from '@/core/assets/control-tower.svg?raw';

import { useUser } from '@/modules/auth/store/user.store';
import { useRoute } from 'vue-router';
import { useFindParticipantDialog } from '@/modules/recruitment/components/FindParticipantDialog.ts';
import { useImportStudyDialog } from '@/modules/recruitment/components/ImportStudyDialog/ImportStudyDialog.ts';
import { useGlobalStatsDialog } from '@/modules/recruitment/components/GlobalStatsDialog';

const { title, version } = storeToRefs(useAppStore());

const route = useRoute();
const isJobsListRoute = computed(() => {
  return route.name == 'allrecruitment';
});

const titleClass = computed(() => {
  if (title.value.length >= 115) {
    return 'text-base';
  }
  if (title.value.length >= 100) {
    return 'text-lg';
  }
  if (title.value.length >= 85) {
    return 'text-xl';
  }
  if (title.value.length >= 70) {
    return 'text-2xl';
  }
  return '';
});

const user = useUser();
const participantDialog = useFindParticipantDialog();
const statsDialog = useGlobalStatsDialog();
const importDialog = useImportStudyDialog();
const openImportDialog = () => {
  importDialog.open();
};

const openGlobalStatsDialog = () => {
  statsDialog.open();
};

const openFindParticipantDialog = () => {
  participantDialog.open();
};
</script>

<template>
  <LayoutDefaultHeader>
    <template #start>
      <div class="flex h-16 items-stretch">
        <div class="flex-initial w-40 pl-4 flex items-stretch">
          <RouterLink :to="{ name: 'home' }" class="flex items-center">
            <img
              src="../../assets/getwhy-logo.svg"
              alt="GetWhy - Recruitment Cockpit"
              :title="version"
              class="mr-12 dark:invert"
            />
          </RouterLink>
        </div>
      </div>
      <div class="flex-auto flex items-center">
        <slot>
          <h1 class="m-0" :class="titleClass" v-if="title">{{ title }}</h1>
        </slot>
      </div>
    </template>
    <template #end>
      <div
        class="card flex justify-end space-x-2 flex-nowrap items-center w-full"
      >
        <Button
          text
          @click="openFindParticipantDialog"
          icon="pi pi-search"
          severity="primary"
          label="Find participant"
          class="mr-2 outline-solid"
        />
        <Button
          v-if="user.ephemeral.beta && isJobsListRoute"
          text
          @click="openImportDialog"
          :icon="PrimeIcons.CLOUD_DOWNLOAD"
          severity="primary"
          label="Import"
          class="mr-2 outline-solid"
        />
        <Button
          v-if="isJobsListRoute"
          text
          @click="openGlobalStatsDialog"
          icon="pi pi-chart-bar"
          severity="primary"
          label=""
          alt="View global recruitment stats"
          title="View global recruitment stats"
          class="mr-2 outline-solid"
        />
        <Button
          v-if="user.data?.operator"
          :title="'Tower Control'"
          text
          as="RouterLink"
          :to="{ name: 'tc.home' }"
          severity="primary"
          class="mr-2 outline-solid"
        >
          <template #icon>
            <span
              class="inline-block text-xl leading-none"
              v-html="controlTowerLogo"
            />
          </template>
        </Button>

        <UserProfileMenu />
      </div>
    </template>
  </LayoutDefaultHeader>
</template>

<style scoped></style>
